/**
 * @file contactForm.component.tsx
 * @path apps/website/src/features/contact/components/contactForm.component.tsx
 * @description Pure presentational form component.
 */

import { cn } from "@sirbastians/utils";
import { Loader2 } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { ContactFormData } from "../contact.types";

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from "@sirbastians/ui";

interface ContactFormProps {
  facade: {
    form: UseFormReturn<ContactFormData>;
    isSubmitting: boolean;
    actions: {
      submit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    };
  };
  className?: string;
}

export const ContactForm = ({ facade, className }: ContactFormProps) => {
  const { form, isSubmitting, actions } = facade;

  return (
    <Card
      className={cn(
        "bg-card border-border rounded-2xl overflow-hidden h-full flex flex-col",
        className,
      )}
    >
      <Form {...form}>
        <form onSubmit={actions.submit} className="flex flex-col h-full">
          <CardHeader className="pb-6 pt-8 px-8">
            <CardTitle className="font-montserrat text-2xl">
              Send a Message
            </CardTitle>
            <CardDescription className="font-open-sans text-base">
              Fill out the form and I'll respond shortly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 px-8 flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-montserrat text-[10px] uppercase tracking-wider text-muted-foreground">
                      First Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="neuro-well bg-secondary border-transparent focus:ring-accent"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-montserrat text-[10px] uppercase tracking-wider text-muted-foreground">
                      Last Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="neuro-well bg-secondary border-transparent focus:ring-accent"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-montserrat text-[10px] uppercase tracking-wider text-muted-foreground">
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      className="neuro-well bg-secondary border-transparent focus:ring-accent"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-montserrat text-[10px] uppercase tracking-wider text-muted-foreground">
                    Message
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      className="neuro-well bg-secondary border-transparent focus:ring-accent resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="px-8 pb-8 pt-4">
            <Button
              type="submit"
              variant="accent"
              size="lg"
              className="w-full font-montserrat tracking-wide rounded-xl"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...
                </>
              ) : (
                "Send Message"
              )}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};
